import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CuttingProjectListItem, CuttingProjectStatus } from '@pomoshnik/shared';
import { CuttingLayout } from './CuttingLayout';
import { buildHistoryCsv } from './lib/historyCsv';
import { useCuttingStore } from './state/useCuttingStore';

/**
 * Block 14 «Раскрой» — Экран 2.14.3 «История» (`history.html`). PR #2-UI + PR #3-DB.
 *
 * Обвязка прототипа: section-title, поиск, чипы-фильтры со счётчиками,
 * «Экспорт CSV», FAB «+». Данные — реальный список из стора (`loadProjects`,
 * PR #3-DB EP-1) под MSW/Edge. Пусто → empty-state. Вёрстка не менялась
 * (state-связка, ADR-0065 / handover §6.5).
 */

type HistoryStatus = 'draft' | 'sent' | 'done' | 'archive';

export interface HistoryProject {
  id: string;
  name: string;
  material: string;
  parts: number;
  kim: number;
  date: string;
  status: HistoryStatus;
}

const FILTERS: { id: 'all' | HistoryStatus; label: string }[] = [
  { id: 'all', label: 'Все' },
  { id: 'draft', label: 'Черновики' },
  { id: 'sent', label: 'Отправлены' },
  { id: 'done', label: 'Готовы' },
  { id: 'archive', label: 'Архив' },
];

/** Сырой статус-ключ → русская метка бейджа (вербатим из history.html). */
const STATUS_LABEL: Record<HistoryStatus, string> = {
  draft: 'Черновик',
  sent: 'Отправлено',
  done: 'Готово',
  archive: 'Архив',
};

/** Контракт-статус → UI-статус чипов (completed→done, archived→archive). */
const STATUS_TO_UI: Record<CuttingProjectStatus, HistoryStatus> = {
  draft: 'draft',
  sent: 'sent',
  completed: 'done',
  archived: 'archive',
};

/**
 * Элемент списка (контракт) → строка истории.
 * ⚠️ `parts` берётся из `sheetCount` (метаданные списка TRD §4.2 несут
 * `sheet_count`, не количество деталей). Слот «N дет.» — `partCount` добавим
 * ADR-амендментом 0065 при эпике Результатов (B-207); контракт не меняем без ADR.
 */
function toHistoryProject(item: CuttingProjectListItem): HistoryProject {
  return {
    id: item.id,
    name: item.name,
    material: item.materialText,
    parts: item.sheetCount ?? 0,
    kim: item.lastKim ?? 0,
    date: item.updatedAt.slice(0, 10),
    status: STATUS_TO_UI[item.status],
  };
}

export function CuttingHistory() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | HistoryStatus>('all');
  const [query, setQuery] = useState('');

  const storeProjects = useCuttingStore((s) => s.projects);
  const loadProjects = useCuttingStore((s) => s.loadProjects);
  const loadProject = useCuttingStore((s) => s.loadProject);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  // Тап по карточке → загрузка проекта в стор → рабочий экран (прототип openProject).
  // При сбое (нет backend/auth) не падаем и не уходим на пустой экран.
  const openProject = async (id: string) => {
    try {
      await loadProject(id);
      navigate('/cutting/project');
    } catch (err) {
      if (import.meta.env.DEV) console.error('[cutting] openProject failed', err);
    }
  };

  const projects = useMemo(() => storeProjects.map(toHistoryProject), [storeProjects]);

  const counts = useMemo(() => {
    const c: Record<'all' | HistoryStatus, number> = {
      all: projects.length,
      draft: 0,
      sent: 0,
      done: 0,
      archive: 0,
    };
    for (const p of projects) c[p.status] += 1;
    return c;
  }, [projects]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return projects.filter((p) => {
      if (filter !== 'all' && p.status !== filter) return false;
      if (!q) return true;
      return p.name.toLowerCase().includes(q) || p.material.toLowerCase().includes(q);
    });
  }, [projects, filter, query]);

  // Скачивание текущего видимого списка как CSV (эталон — exportRegistryCSV в history.html).
  // BOM добавляется здесь, чистый CSV-текст строит buildHistoryCsv.
  const handleExportCsv = () => {
    const csv = buildHistoryCsv(visible);
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `История_проектов_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <CuttingLayout title="Сохранённые проекты" mobileHeaderless>
      <div className="sc-section-title">История проектов раскроя</div>

      <div className="sc-toolbar">
        <input
          type="search"
          className="sc-input medium"
          placeholder="🔍 Поиск (имя, клиент, материал)"
          aria-label="Поиск проектов"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: 1, maxWidth: 300 }}
        />
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`sc-filter-chip${f.id === filter ? ' active' : ''}`}
            aria-pressed={f.id === filter}
            onClick={() => setFilter(f.id)}
          >
            {f.label} ({counts[f.id]})
          </button>
        ))}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={handleExportCsv}
          disabled={visible.length === 0}
        >
          📊 Экспорт CSV
        </button>
      </div>

      <div className="sc-list">
        {visible.length === 0 ? (
          <div className="sc-list-empty">
            <p style={{ margin: '0 0 16px' }}>Пока нет сохранённых раскроев.</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/cutting/project')}
            >
              Создать раскрой
            </button>
          </div>
        ) : (
          visible.map((p) => (
            <div key={p.id} className="sc-list-item">
              <div
                className="list-item-body"
                role="button"
                tabIndex={0}
                aria-label={`Открыть проект ${p.name}`}
                onClick={() => void openProject(p.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    void openProject(p.id);
                  }
                }}
              >
                <div className="list-item-title">
                  <span className={`status-badge ${p.status}`}>{STATUS_LABEL[p.status]}</span>
                  {p.name}
                </div>
                <div className="list-item-meta">
                  {p.material} · {p.parts} дет. · КИМ {p.kim || '—'} · {p.date}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <button
        type="button"
        className="sc-fab"
        aria-label="Новый проект"
        onClick={() => navigate('/cutting')}
      >
        +
      </button>
    </CuttingLayout>
  );
}
